import { useState } from "react";
import OverlappingDonutChart from "./overlapping_donut_chart";
import { KingdomsData, KingdomKey, KINGDOMS } from "../../types/kingdoms";
import { Icon, IconName, IconSize } from "@arise-biodiversity/components";

interface MobileKingdomChartsProps {
  data: KingdomsData;
  loading: boolean;
}

export default function MobileKingdomCharts({
  data,
  loading,
}: MobileKingdomChartsProps) {
  const [openKey, setOpenKey] = useState<KingdomKey | null>(null);

  if (loading) {
    return <div className="loading-state">Loading charts...</div>;
  }

  return (
    <>
      <div className="charts-legenda">
        <p className="legenda-item arise">ARISE</p>
        <p className="legenda-item other">Other organizations</p>
        <p className="legenda-item shared">Shared</p>
      </div>
      <div className="mobile-kingdom-charts">
        {(Object.keys(KINGDOMS) as KingdomKey[]).map((key) => {
          const kingdom = data[key];
          if (!kingdom) return null;

          const isOpen = openKey === key;

          const handleToggle = () => {
            setOpenKey(isOpen ? null : key);
          };

          return (
            <div key={key} className="mobile-kingdom-charts-accordion">
              <button
                className="mobile-kingdom-charts-accordion-top"
                onClick={handleToggle}
                aria-expanded={isOpen}
              >
                <div className="accordion-top-left">
                  <OverlappingDonutChart
                    size={60}
                    arisePercent={kingdom.arise}
                    otherPercent={kingdom.other}
                    totalPercent={kingdom.total}
                  />
                  <span>
                    {kingdom.displayName} {kingdom.total}%
                  </span>
                </div>
                {/* {isOpen ? <ChevronDown /> : <ChevronRight />} */}
                <div
                  className={`accordion-icon-container${isOpen ? " expand" : ""}`}
                >
                  <Icon name={IconName.CHEVRON_RIGHT} size={IconSize.LARGE} />
                </div>
              </button>

              {isOpen && (
                <div className="charts-popover w-100 px-3">
                  <div className="charts-popover-top">
                    <div className="charts-popover-top-left">
                      <h1 className="text-xl font-semibold">
                        <a href={`/?q=${kingdom.displayName}&showfrom=kingdom`}>
                          {kingdom.displayName}
                        </a>
                      </h1>
                      <p>Total coverage: {kingdom.total}%</p>
                    </div>
                    <p className="total-species">
                      {`${new Intl.NumberFormat("nl-NL").format(
                        kingdom.totalSpecies,
                      )} species`}
                    </p>
                  </div>

                  <div className="popover-value-container">
                    <p className="percent-value arise">{kingdom.arise}%</p>
                    <div>
                      <h3>
                        ARISE
                        <span>
                          {` (${new Intl.NumberFormat("nl-NL").format(
                            kingdom.totalAriseSpecies,
                          )} species)`}
                        </span>
                      </h3>
                      <p>{`
                        Barcodes of Dutch species belonging to the kingdom
                        ${kingdom.displayName} that are covered by ARISE
                        consortium partners.`}
                      </p>
                    </div>
                  </div>

                  <div className="popover-value-container">
                    <p className="percent-value other">{kingdom.other}%</p>
                    <div>
                      <h3>
                        Other organizations
                        <span>
                          {` (${new Intl.NumberFormat("nl-NL").format(
                            kingdom.totalOtherSpecies,
                          )} species)`}
                        </span>
                      </h3>
                      <p>{`Barcodes of Dutch species belonging to the kingdom
                        ${kingdom.displayName} that are covered by other
                        organisations inside and outside the Netherlands.`}</p>
                    </div>
                  </div>

                  <div className="popover-value-container">
                    <p className="percent-value shared">{kingdom.shared}%</p>
                    <div>
                      <h3>
                        Shared
                        <span className="text-xs text-gray-500">
                          {` (${new Intl.NumberFormat("nl-NL").format(
                            kingdom.totalSharedSpecies,
                          )} species)`}
                        </span>
                      </h3>
                      <p>
                        {`Barcodes of Dutch species belonging to the kingdom ${kingdom.displayName} that are covered by both ARISE and other organizations. This brings the `}
                        <strong>{`total species uniquely covered by ARISE to ${kingdom.uniqueArisePercent}%`}</strong>.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
